import { Component, ElementRef, Inject, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ClaimDocumentService } from 'src/app/core/services/claim_document.service';
import { CameraService } from '../document-kyc-upload-modal/services/camera.service';
import { UploadReturn } from 'src/app/core/services/base/document.service';

export interface CameraCaptureDialogData {
  bucket: string;
  name: string;
  ref: string; // claim_id
  question_id?: string;
  facingMode: 'user' | 'environment';
  hasMultipleCameras: boolean;
}

export interface CameraCaptureResult {
  status: 'COMPLETED' | 'CANCELLED' | 'ERROR';
  value?: string; // S3 path
  error?: string;
}

@Component({
  selector: 'app-camera-capture-modal',
  templateUrl: './camera-capture-modal.component.html',
  styleUrls: ['./camera-capture-modal.component.scss'],
})
export class CameraCaptureModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('previewImage', { static: false }) previewImage!: ElementRef<HTMLImageElement>;

  // States: 'camera' | 'preview' | 'uploading' | 'success' | 'error'
  currentState: 'camera' | 'preview' | 'uploading' | 'success' | 'error' = 'camera';
  
  facingMode: 'user' | 'environment';
  hasMultipleCameras: boolean = false;
  
  capturedPhotoBlob?: Blob;
  capturedPhotoDataUrl?: string;
  
  uploadedPath?: string;
  errorMessage?: string;

  result: CameraCaptureResult = {
    status: 'CANCELLED',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CameraCaptureDialogData,
    private dialogRef: MatDialogRef<CameraCaptureModalComponent>,
    private cameraService: CameraService,
    private claimDocumentService: ClaimDocumentService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.facingMode = data.facingMode || 'environment';
    this.hasMultipleCameras = data.hasMultipleCameras || false;
  }

  ngAfterViewInit(): void {
    // Start camera automatically when modal opens
    this.startCamera();
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  async startCamera(): Promise<void> {
    try {
      const stream = await this.cameraService.startCamera(this.facingMode);
      
      // Wait for video element to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = stream;
        this.currentState = 'camera';
      }
    } catch (error: any) {
      console.error('Error starting camera:', error);
      const errorMsg = this.translate.instant(error.message) || this.translate.instant('FailedToStartCamera');
      this.showError(errorMsg);
    }
  }

  stopCamera(): void {
    this.cameraService.stopCamera();
  }

  async capturePhoto(): Promise<void> {
    try {
      if (!this.videoElement || !this.videoElement.nativeElement) {
        throw new Error('Video element not found');
      }

      const blob = await this.cameraService.capturePhoto(
        this.videoElement.nativeElement,
        0.95
      );

      this.capturedPhotoBlob = blob;
      
      // Convert to data URL for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        this.capturedPhotoDataUrl = reader.result as string;
        this.currentState = 'preview';
        this.stopCamera();
      };
      reader.readAsDataURL(blob);
    } catch (error: any) {
      console.error('Error capturing photo:', error);
      this.showError(this.translate.instant('FailedToCapturePhoto'));
    }
  }

  async switchCamera(): Promise<void> {
    try {
      const stream = await this.cameraService.switchCamera(this.facingMode);
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = stream;
      }
    } catch (error: any) {
      console.error('Error switching camera:', error);
      this.snackBar.open(
        this.translate.instant('FailedToSwitchCamera'),
        this.translate.instant('Close'),
        { duration: 3000 }
      );
    }
  }

  retakePhoto(): void {
    this.capturedPhotoBlob = undefined;
    this.capturedPhotoDataUrl = undefined;
    this.currentState = 'camera';
    this.startCamera();
  }

  async confirmAndUpload(): Promise<void> {
    if (!this.capturedPhotoBlob) {
      this.showError(this.translate.instant('NoPhotoToUpload'));
      return;
    }

    try {
      this.currentState = 'uploading';

      // Convert blob to File
      const file = new File(
        [this.capturedPhotoBlob],
        `camera-photo-${Date.now()}.jpg`,
        { type: 'image/jpeg' }
      );

      // Upload to S3 using the same service as file upload
      const uploadResult: UploadReturn = await this.claimDocumentService.upload(
        file,
        this.data.ref,
        this.data.name,
        this.data.bucket,
        undefined,
        this.data.question_id
      );

      if (uploadResult.data?.path) {
        this.uploadedPath = uploadResult.data.path;
        this.result = {
          status: 'COMPLETED',
          value: uploadResult.data.path,
        };
        this.currentState = 'success';
        
        // Auto-close after success
        setTimeout(() => {
          this.close();
        }, 1000);
      } else {
        throw new Error(this.translate.instant('UploadFailedNoPath'));
      }
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      this.showError(this.translate.instant('FailedToUploadPhoto'));
    }
  }

  cancel(): void {
    this.result = {
      status: 'CANCELLED',
    };
    this.close();
  }

  close(): void {
    this.stopCamera();
    this.dialogRef.close(this.result);
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.currentState = 'error';
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  }
}
